import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Gift, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicContactLead() {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [contactConsent, setContactConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [giveaway, setGiveaway] = useState(true);
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !last.trim() || !email.trim()) {
      toast.error("First name, last name, and email are required.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (zip && !/^\d{5}(-\d{4})?$/.test(zip.trim())) {
      toast.error("Enter a 5-digit ZIP code.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/contact-leads`, {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        zip: zip.trim() || null,
        contact_consent: contactConsent,
        marketing_consent: marketingConsent,
        giveaway_entry: giveaway,
        source: "hcwi_contact_form",
        hp_field: hp,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F8F6] via-white to-[#F9F8F6]" data-testid="public-contact-lead-page">
      <header className="border-b border-[#E5E5E0] bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <HarborWellFormHeader
            eyebrow="Stay connected"
            size="md"
            testId="public-contact-lead-brand-header"
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-24">
        {done ? (
          <section className="space-y-5 text-center" data-testid="contact-lead-thank-you">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#DCFCE7] text-[#166534] flex items-center justify-center">
              <MailCheck className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-3xl font-semibold text-[#0F172A]">You&apos;re on the list.</h1>
            <p className="text-[#334155]">
              Thanks for staying connected with HarborWell. We&apos;ll be in touch soon.
            </p>
            <Link to="/" className="inline-block text-sm text-[#1E3A8A] underline" data-testid="contact-lead-home-link">
              Return home
            </Link>
          </section>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="contact-lead-form">
            <div>
              <div className="hw-eyebrow mb-2">Optional · separate from your survey</div>
              <h1 className="font-display text-3xl font-semibold text-[#0F172A]">Stay Connected & Enter Our Community Giveaway</h1>
              <p className="text-[#334155] mt-2">
                Enter our $200 Gas Card Giveaway, receive HarborWell updates, learn about volunteer opportunities,
                or request a HarborWell team member to contact you.
              </p>
              <div className="mt-3 inline-flex items-start gap-2 bg-[#FEF3C7] border border-[#FCD34D] rounded-xl px-3 py-2 text-xs text-[#92400E]">
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>Your contact info is collected separately and is <strong>not</strong> linked to your anonymous survey responses.</div>
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text" name="hp_field" value={hp} onChange={(e) => setHp(e.target.value)}
              tabIndex={-1} aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", top: "-9999px" }} autoComplete="off"
            />

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="First name" required>
                <input
                  type="text" value={first} onChange={(e) => setFirst(e.target.value)}
                  data-testid="lead-first-name"
                  className="hw-input" required maxLength={100}
                />
              </Field>
              <Field label="Last name" required>
                <input
                  type="text" value={last} onChange={(e) => setLast(e.target.value)}
                  data-testid="lead-last-name"
                  className="hw-input" required maxLength={100}
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="lead-email"
                  className="hw-input" required maxLength={200}
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  data-testid="lead-phone"
                  className="hw-input" maxLength={30}
                />
              </Field>
              <Field label="ZIP Code (optional)">
                <input
                  type="text" value={zip} onChange={(e) => setZip(e.target.value)}
                  data-testid="lead-zip"
                  className="hw-input" maxLength={10}
                />
              </Field>
            </div>

            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 space-y-3">
              <Toggle
                label="Would you like someone from HarborWell to contact you?"
                checked={contactConsent} onChange={setContactConsent}
                testId="lead-contact-consent"
              />
              <Toggle
                label="Would you like to receive HarborWell news and updates?"
                checked={marketingConsent} onChange={setMarketingConsent}
                testId="lead-marketing-consent"
              />
              <Toggle
                label={<span className="inline-flex items-center gap-1.5"><Gift className="w-4 h-4 text-[#92400E]" /> Enter me into the $200 Gas Card Giveaway.</span>}
                checked={giveaway} onChange={setGiveaway}
                testId="lead-giveaway"
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <Link to="/" className="px-4 py-2 text-sm text-[#334155]">Cancel</Link>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit
              </button>
            </div>
          </form>
        )}
      </main>

      <style>{`
        .hw-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border-radius: 0.75rem;
          border: 1px solid #E5E5E0;
          background: #FFFFFF;
          font-size: 0.875rem;
          color: #0F172A;
        }
        .hw-input:focus { outline: none; border-color: #1E3A8A; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#334155] mb-1">
        {label}{required && <span className="text-[#991B1B] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testId}
      aria-pressed={checked}
      className="w-full flex items-start justify-between gap-3 text-left"
    >
      <span className="text-sm text-[#0F172A] flex-1">{label}</span>
      <span className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-[#1E3A8A]" : "bg-[#CBD5E1]"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
    </button>
  );
}
